"use client";

/**
 * Progression des quêtes — plein écran après le récap de leçon (et après la
 * célébration de série), pattern « Daily Quest update! » de Duolingo : une
 * carte par quête du jour, la barre se remplit de l'ancienne valeur vers la
 * nouvelle, coche à la complétion, et un « +N » s'envole vers le compteur du
 * badge du mois quand une quête vient d'être terminée.
 *
 * Parité 1:1 avec reps-app/src/features/lesson/QuestsCelebration.tsx : même
 * diff (lib/quests/questProgress), mêmes titres, même chronologie.
 *
 * Purement présentationnel : tout ce qui est affiché a DÉJÀ été crédité par
 * le serveur, cet écran ne fait que le raconter.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations, useLocale } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import RepsContinueButton from "@/components/Reps/RepsContinueButton";
import { questIconSrc, questLabelKey, monthName } from "@/lib/quests/questMeta";

/**
 * Compteur qui monte vers `to` (easing cubic-out), déclenché à `delayMs`.
 * Les barres se remplissent en 750 ms : un nombre qui saute à l'arrivée dès
 * le départ raconterait la fin avant la fin.
 */
function useCountUp(from, to, delayMs, durationMs, enabled) {
    const [value, setValue] = useState(enabled ? from : to);
    const rafRef = useRef(null);
    useEffect(() => {
        if (!enabled) return undefined;
        let timer = setTimeout(() => {
            const start = Date.now();
            const tick = () => {
                const p = Math.min(1, (Date.now() - start) / durationMs);
                const eased = 1 - Math.pow(1 - p, 3);
                setValue(Math.round(from + (to - from) * eased));
                if (p < 1) rafRef.current = requestAnimationFrame(tick);
            };
            rafRef.current = requestAnimationFrame(tick);
        }, delayMs);
        return () => {
            clearTimeout(timer);
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
        };
        // Chronologie figée à l'affichage.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return value;
}

/* Chronologie, en ms depuis l'affichage de l'écran. */
const CARD_STAGGER_MS = 110;
/** Départ du remplissage de la première barre (après l'entrée des cartes). */
const BAR_START_MS = 620;
/** Décalage entre deux barres animées. */
const BAR_STAGGER_MS = 420;
/** Coche de complétion, après le départ de sa barre. */
const BAR_COMPLETE_MS = 520;
/** Vol du « +N » avant son atterrissage sur le compteur du badge. */
const CHIP_FLY_MS = 360;
/** Garde anti-double-tap : le CTA occupe les pixels de l'écran précédent. */
const TAP_GUARD_MS = 600;

const BAR_FILL_S = 0.75;

export default function RepsQuestsCelebration({
    update,
    onContinue,
    leaving,
    onSound,
}) {
    const t = useTranslations();
    const locale = useLocale();
    const reduce = useReducedMotion();
    // Chronologie FIGÉE à l'affichage : une bascule du réglage en cours
    // d'écran ne doit ni rejouer ni geler l'animation à moitié.
    const [still] = useState(() => !!reduce);

    const { cards, deltas, completedCount, badge } = update;

    // Départ de la séquence du badge : une fois toutes les barres ANIMÉES
    // remplies (les cartes statiques ne comptent pas dans la chronologie).
    const badgeAtMs =
        BAR_START_MS +
        Math.max(0, deltas.length - 1) * BAR_STAGGER_MS +
        BAR_COMPLETE_MS +
        250;

    // Décalage de remplissage par carte animée (null = carte statique).
    const cardInfos = useMemo(
        () =>
            cards.map((card, index) => {
                if (!card.changed) return { card, fillAtMs: null };
                const changedBefore = cards
                    .slice(0, index)
                    .filter((c) => c.changed).length;
                return {
                    card,
                    fillAtMs: BAR_START_MS + changedBefore * BAR_STAGGER_MS,
                };
            }),
        [cards],
    );

    // Titre par priorité (calqué sur Duolingo) : tout le jeu du jour terminé >
    // une quête terminée avec badge visible (le titre annonce le +N qui va
    // voler) > terminée sans badge (le nombre) > simple progression.
    const allDone =
        cards.length > 0 && cards.every((c) => c.quest.completed === true);
    const title = allDone
        ? t("reps_quests_all_done_title")
        : completedCount > 0 && badge
          ? t("reps_quests_badge_plus_title", { count: completedCount })
          : completedCount > 0
            ? t("reps_quests_completed_title", { count: completedCount })
            : t("reps_quests_update_title");

    const [mountedAt] = useState(() => Date.now());
    const handleContinue = () => {
        if (Date.now() - mountedAt < TAP_GUARD_MS) return;
        onContinue();
    };

    // Résumé lu d'une traite par les lecteurs d'écran : sans lui, ils
    // annonceraient une suite de nombres au fil des animations. Même doctrine
    // que l'app mobile, qui annonce le titre puis chaque quête.
    const ofWord = locale === "en" ? "of" : "sur";
    const summary = [
        title,
        ...cards.map(
            (c) =>
                `${t(questLabelKey(c.quest), { value: c.quest.targetValue })}, ${c.to} ${ofWord} ${c.quest.targetValue}`,
        ),
    ].join(". ");
    // La région n'est REMPLIE qu'après le montage : une région live déjà
    // pleine au premier rendu n'annonce rien, elle ne signale que ses
    // changements. Le focus vient sur l'écran pour la même raison.
    const [announce, setAnnounce] = useState("");
    const rootRef = useRef(null);
    useEffect(() => {
        rootRef.current?.focus({ preventScroll: true });
        const id = setTimeout(() => setAnnounce(summary), 150);
        return () => clearTimeout(id);
        // Résumé figé à l'affichage.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={rootRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            className="fixed inset-0 z-[60] overflow-y-auto bg-[#f5f5fa] text-gray-900 outline-hidden dark:bg-[#192734] dark:text-white"
        >
            <p className="sr-only" role="status" aria-live="polite">
                {announce}
            </p>
            <div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center px-6 py-10">
                <motion.h2
                    initial={still ? false : { opacity: 0, y: 14, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 240, damping: 22 }}
                    className={`text-center text-3xl font-extrabold ${
                        completedCount > 0
                            ? "text-orange-500"
                            : "text-gray-900 dark:text-white"
                    }`}
                >
                    {title}
                </motion.h2>

                {badge ? (
                    <BadgeCard
                        badge={badge}
                        completedCount={completedCount}
                        startDelayMs={badgeAtMs}
                        still={still}
                        locale={locale}
                        onSound={onSound}
                    />
                ) : null}

                <div className="mt-5 flex flex-col gap-3">
                    {cardInfos.map(({ card, fillAtMs }, index) => (
                        <QuestCard
                            key={String(card.quest.id)}
                            card={card}
                            entranceIndex={index}
                            fillAtMs={fillAtMs}
                            still={still}
                            onSound={onSound}
                        />
                    ))}
                </div>

                <RepsContinueButton
                    onClick={handleContinue}
                    loading={leaving}
                    className="mt-8"
                >
                    {t("reps_continue")}
                </RepsContinueButton>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Carte d'une quête. Toutes les quêtes du jour sont listées (parité    */
/* Duolingo) ; seules celles que la leçon a fait bouger s'animent.      */
/* ------------------------------------------------------------------ */

function QuestCard({ card, entranceIndex, fillAtMs, still, onSound }) {
    const t = useTranslations();
    const { quest, from, to, changed, completedNow } = card;
    const target = Math.max(1, quest.targetValue);
    const animated = changed && !still && fillAtMs != null;

    // Largeur affichée : on part de l'ancienne valeur puis on remplit.
    const [frac, setFrac] = useState(
        animated ? Math.min(1, from / target) : Math.min(1, to / target),
    );
    // Coche : statique si la quête était déjà finie avant la leçon.
    const staticCheck = quest.completed === true && !animated;
    const [showCheck, setShowCheck] = useState(staticCheck);
    // Le compteur monte en même temps que SA barre se remplit.
    const value = useCountUp(
        from,
        to,
        fillAtMs ?? 0,
        BAR_FILL_S * 1000,
        animated,
    );

    useEffect(() => {
        if (!animated) return undefined;
        const timers = [
            setTimeout(() => {
                setFrac(Math.min(1, to / target));
                // Un son par barre qui part : c'est ce qui donne le rythme.
                if (onSound) onSound("middle");
            }, fillAtMs),
        ];
        if (completedNow) {
            timers.push(
                setTimeout(
                    () => setShowCheck(true),
                    fillAtMs + BAR_COMPLETE_MS,
                ),
            );
        }
        return () => timers.forEach(clearTimeout);
        // Chronologie figée à l'affichage.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Jaune pendant, émeraude une fois finie (parité de la liste de quêtes).
    const done = quest.completed === true;
    const label = t(questLabelKey(quest), { value: quest.targetValue });
    const barColor =
        done && (showCheck || !animated) ? "bg-emerald-400" : "bg-yellow-400";

    return (
        <motion.div
            initial={still ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.28,
                ease: "easeOut",
                delay: still ? 0 : (entranceIndex * CARD_STAGGER_MS) / 1000,
            }}
            className="rounded-2xl border-2 border-gray-100 bg-white p-4 dark:border-white/10 dark:bg-[#253340]"
        >
            <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-purple-50 dark:bg-purple-500/10">
                    <Image
                        src={questIconSrc(quest)}
                        alt=""
                        width={18}
                        height={18}
                    />
                </span>
                <span className="min-w-0 flex-1 text-[15px] font-semibold leading-5">
                    {label}
                </span>
                {/* Le compteur vit À CÔTÉ du libellé, jamais dans la barre :
                    posé dessus, il finissait sur le remplissage jaune ou
                    émeraude, illisible en sombre au moment précis où la quête
                    se termine. Même placement que la carte du badge. */}
                <span className="shrink-0 text-sm font-bold tabular-nums text-gray-500 dark:text-grayDark-300">
                    {value}/{quest.targetValue}
                </span>
                {done ? (
                    <motion.span
                        initial={
                            staticCheck ? false : { scale: 0.4, opacity: 0 }
                        }
                        animate={
                            showCheck
                                ? { scale: 1, opacity: 1 }
                                : { scale: 0.4, opacity: 0 }
                        }
                        transition={{
                            type: "spring",
                            stiffness: 520,
                            damping: 16,
                        }}
                        className="grid shrink-0 place-items-center"
                    >
                        <SolarIcon
                            name="check-circle-bold"
                            className="text-2xl text-emerald-500"
                        />
                    </motion.span>
                ) : null}
            </div>

            <div
                role="progressbar"
                aria-label={label}
                aria-valuemin={0}
                aria-valuemax={quest.targetValue}
                aria-valuenow={to}
                className="mt-3 h-4 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10"
            >
                <motion.div
                    initial={false}
                    animate={{ width: `${Math.round(frac * 100)}%` }}
                    transition={
                        still
                            ? { duration: 0 }
                            : { duration: BAR_FILL_S, ease: "easeOut" }
                    }
                    className={`h-full rounded-full ${barColor} transition-colors duration-300`}
                />
            </div>
        </motion.div>
    );
}

/* ------------------------------------------------------------------ */
/* Badge du mois : le « +N » s'envole vers le compteur                  */
/* ------------------------------------------------------------------ */

function BadgeCard({
    badge,
    completedCount,
    startDelayMs,
    still,
    locale,
    onSound,
}) {
    const t = useTranslations();
    const { current, fromCompleted, awardedBefore } = badge;
    const target = Math.max(1, current.questTarget);
    const landAtMs = startDelayMs + CHIP_FLY_MS;
    // Gagné PAR CETTE LEÇON (on bascule en émeraude) vs déjà gagné avant (état
    // de repos, sans rejouer la bascule à chaque quête du reste du mois).
    const justAwarded = current.isAwarded === true && !awardedBefore;

    // hidden -> fly (le +N monte vers le compteur) -> landed (il s'y fond).
    const [chip, setChip] = useState(still ? "landed" : "hidden");
    const [landed, setLanded] = useState(still);
    // Le compteur ne tique qu'à l'atterrissage du « +N » : c'est lui qui
    // raconte l'ajout, pas un nombre déjà arrivé.
    const count = useCountUp(
        fromCompleted,
        current.questCompleted,
        landAtMs,
        BAR_FILL_S * 1000,
        !still,
    );
    const [frac, setFrac] = useState(
        still
            ? Math.min(1, current.questCompleted / target)
            : Math.min(1, fromCompleted / target),
    );

    useEffect(() => {
        if (still) return undefined;
        const timers = [
            setTimeout(() => setChip("fly"), startDelayMs),
            setTimeout(() => {
                setChip("landed");
                setLanded(true);
                setFrac(Math.min(1, current.questCompleted / target));
                // Le « +N » se pose sur le compteur : c'est le point d'orgue.
                if (onSound) onSound("secondChance");
            }, landAtMs),
        ];
        return () => timers.forEach(clearTimeout);
        // Chronologie figée à l'affichage.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const remaining = Math.max(0, current.questTarget - current.questCompleted);
    const awardedNow = current.isAwarded === true && (landed || !justAwarded);

    return (
        <motion.div
            initial={still ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: "easeOut", delay: 0.15 }}
            className="mt-6 rounded-2xl border-2 border-gray-100 bg-white p-4 dark:border-white/10 dark:bg-[#253340]"
        >
            <div className="flex items-center gap-3.5">
                <motion.span
                    initial={false}
                    animate={landed && !still ? { scale: [1, 1.12, 1] } : {}}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                    className="relative grid h-14 w-14 shrink-0 place-items-center"
                >
                    {current.imageUrl ? (
                        <Image
                            src={current.imageUrl}
                            alt=""
                            width={56}
                            height={56}
                            // Non gagné = noir et blanc, comme la collection.
                            className={`rounded-full ${
                                current.isAwarded ? "" : "opacity-70 grayscale"
                            }`}
                        />
                    ) : (
                        <span className="grid h-14 w-14 place-items-center rounded-full bg-orange-100 dark:bg-orange-500/15">
                            <SolarIcon
                                name="medal-ribbon-star-bold"
                                className="text-2xl text-orange-500"
                            />
                        </span>
                    )}
                </motion.span>

                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                        {t("reps_badges_title")}
                    </div>
                    <div className="mt-0.5 truncate text-[17px] font-extrabold">
                        {current.name || monthName(current.month, locale)}
                    </div>
                </div>

                <div className="relative flex shrink-0 flex-col items-end">
                    {chip !== "hidden" && !still ? (
                        <motion.span
                            initial={{ opacity: 0, y: 26, scale: 0.5 }}
                            animate={
                                chip === "fly"
                                    ? { opacity: 1, y: 0, scale: 1 }
                                    : { opacity: 0, y: -8, scale: 0.6 }
                            }
                            transition={
                                chip === "fly"
                                    ? {
                                          type: "spring",
                                          stiffness: 380,
                                          damping: 18,
                                      }
                                    : { duration: 0.22, ease: "easeOut" }
                            }
                            className="pointer-events-none absolute -top-7 right-0 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-extrabold text-white"
                        >
                            +{completedCount}
                        </motion.span>
                    ) : null}
                    <span className="text-base font-extrabold tabular-nums text-orange-500">
                        {count}/{current.questTarget}
                    </span>
                </div>
            </div>

            <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={current.questTarget}
                aria-valuenow={current.questCompleted}
                aria-label={t("reps_badges_title")}
                className="mt-3 h-2.5 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10"
            >
                <motion.div
                    initial={false}
                    animate={{ width: `${Math.round(frac * 100)}%` }}
                    transition={
                        still
                            ? { duration: 0 }
                            : { duration: BAR_FILL_S, ease: "easeOut" }
                    }
                    className={`h-full rounded-full transition-colors duration-300 ${
                        awardedNow ? "bg-emerald-500" : "bg-orange-500"
                    }`}
                />
            </div>

            {/* Le statut n'apparaît qu'à l'atterrissage du +N : sinon il
                révèle la fin de l'histoire pendant que le compteur tique. */}
            <motion.p
                initial={still ? false : { opacity: 0 }}
                animate={{ opacity: landed ? 1 : 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className={`mb-0 mt-2 text-xs ${
                    current.isAwarded
                        ? "font-bold text-emerald-500"
                        : "text-gray-500 dark:text-grayDark-400"
                }`}
            >
                {current.isAwarded
                    ? t("reps_badges_earned")
                    : t("reps_badges_remaining", { count: remaining })}
            </motion.p>
        </motion.div>
    );
}
